import { useScrollAnimation } from "@/hooks/useScrollAnimation";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/Button";
import { ArrowRight } from "lucide-react";

// TODO(content): swap for real student stories with consent + photo + outcome metric
const FRAMES = [
    {
        chapter: "The gap",
        title: "Aid didn't cover it. Loans weren't an option.",
        body: "Maria, a first-gen environmental science student at UC Berkeley, was making it work — barely. Books, lab fees, rent. Anything could tip the semester.",
        image: "/screenshots/creator-audience.jpg",
        tag: "Before",
    },
    {
        chapter: "Her people showed up",
        title: "One link. Thirty-two supporters in a week.",
        body: "She shared her Fund Page in the family group chat. Grandma Lupe set up $50/month. Her old soccer coach pitched in. Her aunt's book club did too.",
        image: "/screenshots/donor-options.jpg",
        tag: "The shift",
    },
    {
        chapter: "She stayed in school",
        title: "A steady stream. Posting weekly updates.",
        body: "Maria's Fund Page is now monthly support that covers the gap between aid and reality. She's posting weekly updates from her research — real connection, not a transaction.",
        image: "/screenshots/creator-missionprofile.jpg",
        tag: "Today",
    },
];

const StorySection = () => {
    const { ref, isVisible } = useScrollAnimation(0.05);

    return (
        <section
            ref={ref}
            id="story"
            className="relative py-20 md:py-28 bg-white"
        >
            <div className="max-w-6xl mx-auto px-6">
                <div
                    className={`text-center max-w-3xl mx-auto mb-14 md:mb-16 transition-all duration-700 ${
                        isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"
                    }`}
                >
                    <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary/5 border border-primary/10 mb-4">
                        <span className="text-xs font-semibold tracking-wide text-primary uppercase">
                            A real journey
                        </span>
                    </div>
                    <h2 className="text-3xl md:text-5xl font-extrabold text-primary leading-tight tracking-tight">
                        From "I might have to drop out" to{" "}
                        <span
                            className="bg-clip-text text-transparent"
                            style={{
                                backgroundImage:
                                    "linear-gradient(90deg, hsl(var(--gold)), hsl(var(--success)))",
                            }}
                        >
                            "I'm graduating"
                        </span>
                    </h2>
                    <p className="text-lg text-slate-600 mt-4 max-w-2xl mx-auto">
                        Every Fund Page is a story. Here's one we couldn't stop thinking about.
                    </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
                    {FRAMES.map((frame, i) => (
                        <article
                            key={frame.chapter}
                            className={`group relative bg-white rounded-3xl overflow-hidden border border-slate-200/70 shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all duration-500 ${
                                isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"
                            }`}
                            style={{ transitionDelay: `${i * 120}ms` }}
                        >
                            <div className="relative aspect-[4/3] overflow-hidden">
                                <img
                                    src={frame.image}
                                    alt={frame.title}
                                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                                />
                                <div className="absolute top-3 left-3 px-2.5 py-1 rounded-full bg-white/90 backdrop-blur text-[11px] font-bold text-primary uppercase tracking-wide">
                                    Frame {i + 1} · {frame.tag}
                                </div>
                            </div>
                            <div className="p-6">
                                <p className="text-xs font-bold tracking-wider uppercase text-gold mb-2"
                                   style={{ color: "hsl(var(--gold))" }}>
                                    {frame.chapter}
                                </p>
                                <h3 className="text-xl font-bold text-primary leading-snug mb-2">
                                    {frame.title}
                                </h3>
                                <p className="text-slate-600 leading-relaxed text-[15px]">
                                    {frame.body}
                                </p>
                            </div>
                        </article>
                    ))}
                </div>

                <div className="mt-12 md:mt-14 text-center">
                    <Link to="/fund/demo">
                        <Button
                            size="lg"
                            className="bg-primary hover:bg-primary/90 text-white text-base md:text-lg px-8 py-6 rounded-full font-semibold shadow-lg shadow-primary/20 group"
                        >
                            See a real Fund Page
                            <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
                        </Button>
                    </Link>
                </div>
            </div>
        </section>
    );
};

export default StorySection;
